import { Form, Input, Button, Card, message, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { api } from '../utils/request';
import { useAuthStore } from '../stores/auth';
import LoginBackground from '../components/LoginBackground';
import logoImg from '../assets/kimap-logo.png';

export default function Login() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const res = await api.post<{
        token: string;
        user: any;
      }>('/auth/login', values);

      if (res?.token && res?.user) {
        setAuth(res.token, res.user);
        message.success(t('auth.login.loginSuccess'));
        setTimeout(() => navigate('/maps'), 0);
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      const errorMsg = errorData?.message || error.message || t('auth.login.loginFailed');
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* SVG Background */}
      <LoginBackground />

      {/* Logo */}
      <div className="top-logo">
        <img src={logoImg} alt="Kimap" className="logo-img" />
        <h2>{t('auth.login.title')}</h2>
      </div>

      {/* Login Card */}
      <Card className="auth-card">
        <div className="brand">
          <h1>{t('auth.login.welcomeBack')}</h1>
          <p className="tagline">{t('auth.login.signInSubtitle')}</p>
        </div>

        <Form
          name="login"
          onFinish={onFinish}
          layout="vertical"
          className="auth-form"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: t('auth.login.usernameRequired') }]}
          >
            <Input
              prefix={<UserOutlined className="input-icon" />}
              placeholder={t('auth.login.usernamePlaceholder')}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('auth.login.passwordRequired') }]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder={t('auth.login.passwordPlaceholder')}
              size="large"
            />
          </Form.Item>
          <Form.Item>
            <div className="form-options">
              <Checkbox>{t('auth.login.rememberMe')}</Checkbox>
              <a href="#" className="forgot-link">{t('auth.login.forgotPassword')}</a>
            </div>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading} disabled={loading}>
              {!loading && t('auth.login.signIn')}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer">
          <span>{t('auth.login.noAccount')} </span>
          <Link to="/register" className="auth-link">{t('auth.login.signUp')}</Link>
        </div>
      </Card>

      {/* Back to home */}
      <div className="back-home">
        <Link to="/" className="back-link">
          ← {t('auth.login.backToHome')}
        </Link>
      </div>

      <style>{`
        .auth-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          overflow: hidden;
        }

        /* Top logo */
        .top-logo {
          position: fixed;
          top: 32px;
          left: 32px;
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-img {
          height: 22px;
          width: auto;
        }

        .top-logo h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--color-text-primary);
          letter-spacing: -0.5px;
        }

        /* Auth card */
        .auth-card {
          width: 100%;
          max-width: 420px;
          padding: 0;
          position: relative;
          z-index: 10;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid var(--color-border);
        }

        .auth-card :global(.ant-card-body) {
          padding: 48px 40px;
        }

        /* Brand area */
        .brand {
          text-align: center;
          margin-bottom: 36px;
        }

        .brand h1 {
          font-size: 28px;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 8px 0;
          letter-spacing: -0.5px;
        }

        .tagline {
          font-size: 14px;
          color: var(--color-text-secondary);
          margin: 0;
        }

        /* Form */
        .auth-form {
          margin-bottom: 0;
        }

        .auth-form :global(.ant-form-item) {
          margin-bottom: 20px;
        }

        .auth-form :global(.ant-form-item-label > label) {
          font-weight: 500;
          color: var(--color-text-primary);
          font-size: 14px;
        }

        .input-icon {
          color: var(--color-text-tertiary);
          font-size: 16px;
        }

        .form-options {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: -4px 0 8px 0;
        }

        .form-options :global(.ant-checkbox-wrapper) {
          font-size: 14px;
          color: var(--color-text-secondary);
        }

        .forgot-link {
          color: var(--color-accent);
          font-size: 14px;
          font-weight: 500;
        }

        .forgot-link:hover {
          color: var(--color-accent-dark);
        }

        /* Auth footer */
        .auth-footer {
          text-align: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--color-border);
          color: var(--color-text-secondary);
          font-size: 14px;
        }

        .auth-link {
          color: var(--color-accent);
          font-weight: 600;
          margin-left: 4px;
        }

        .auth-link:hover {
          color: var(--color-accent-dark);
        }

        /* Back to home */
        .back-home {
          position: fixed;
          bottom: 32px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
        }

        .back-link {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--color-text-secondary);
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }

        .back-link:hover {
          color: var(--color-accent);
        }

        /* Responsive */
        @media (max-width: 576px) {
          .auth-card :global(.ant-card-body) {
            padding: 32px 24px;
          }

          .brand h1 {
            font-size: 24px;
          }

          .top-logo {
            top: 24px;
            left: 24px;
          }

          .top-logo h2 {
            font-size: 16px;
          }

          .back-home {
            bottom: 24px;
          }
        }
      `}</style>
    </div>
  );
}
